import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Box,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AppPagination from '../../../components/base/AppPagination';

const PatientTable = ({
  loading,
  currentData,
  currentPage,
  count,
  totalItems,
  itemsPerPage,
  apiUrl,
  onView,
  onEdit,
  onDelete,
  onPageChange,
}) => {
  const getFullImageUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('blob:') || photoPath.startsWith('http')) {
      return photoPath;
    }
    const storageBase = apiUrl ? apiUrl.replace('/api', '') : '';
    if (photoPath.startsWith('storage/')) {
      return `${storageBase}/${photoPath}`;
    }
    return `${storageBase}/${photoPath}`;
  };

  const displayedData =
    typeof currentData === 'function' ? currentData() : currentData;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid #F4F7FE',
      }}
    >
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: '#F4F7FE' }}>
            <TableRow>
              <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                ID-code
              </TableCell>
              <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                PATIENT
              </TableCell>
              <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                LIEU DE RÉSIDENCE
              </TableCell>
              <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                TÉLÉPHONE
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: '#A3AED0', fontWeight: 700 }}
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress
                    size={30}
                    sx={{ my: 4, color: '#4318FF' }}
                  />
                </TableCell>
              </TableRow>
            ) : !displayedData || displayedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 4, color: '#A3AED0' }}
                >
                  Aucun patient trouvé
                </TableCell>
              </TableRow>
            ) : (
              displayedData.filter(Boolean).map((patient) => (
                <TableRow key={patient.id_patient_profile} hover>
                  <TableCell sx={{ color: '#2B3674', fontWeight: 700 }}>
                    {patient.code_patient}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={getFullImageUrl(patient.photo_patient)}
                        sx={{ bgcolor: '#4318FF' }}
                      >
                        {patient.nom_patient?.[0]}
                        {patient.prenom_patient?.[0]}
                      </Avatar>
                      <Box>
                        <Typography sx={{ color: '#2B3674', fontWeight: 700 }}>
                          {patient.nom_patient} {patient.prenom_patient}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                          {patient.email_patient ||
                            patient.client?.email_client ||
                            'Aucun email'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                    {patient.lieu_residence_patient || '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                    {patient.telephone_patient ||
                      patient.client?.phone_client ||
                      '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Voir les détails">
                      <IconButton
                        onClick={() => onView && onView(patient)}
                        sx={{
                          color: '#4318FF',
                          bgcolor: '#F4F7FE',
                          mr: 1,
                          '&:hover': { bgcolor: '#E9EDF7' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Modifier le patient">
                      <IconButton
                        onClick={() => onEdit && onEdit(patient)}
                        sx={{
                          color: '#FFB547',
                          bgcolor: '#FFF8EC',
                          mr: 1,
                          '&:hover': { bgcolor: '#FFEFD1' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Supprimer le patient">
                      <IconButton
                        onClick={() => onDelete && onDelete(patient)}
                        sx={{
                          color: '#C5221F',
                          bgcolor: '#FFF5F5',
                          '&:hover': { bgcolor: '#FFE0E0' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && totalItems > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <AppPagination
            count={count}
            currentPage={currentPage}
            onChange={(page) => {
              if (onPageChange) onPageChange(page);
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default PatientTable;